import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { money, pct, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const TABS = [["", "All"], ["active", "Active"], ["late", "Late"], ["paid_off", "Paid Off"]];
const PAGE_SIZE = 20;

export default function Loans() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get("status") || "";
  const borrowerId = params.get("borrower_id") || "";
  const borrowerName = params.get("borrower_name") || "";
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => { setQ(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    api.get("/loans", { params: { status, search: q, borrower_id: borrowerId, page, page_size: PAGE_SIZE } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [status, q, borrowerId, page]);

  const setStatus = (s) => {
    const next = new URLSearchParams(params);
    s ? next.set("status", s) : next.delete("status");
    setParams(next);
    setPage(1);
  };

  return (
    <div data-testid="loans-page">
      <PageHeader eyebrow="Portfolio" title="Loans" subtitle={`${data.total} loan${data.total === 1 ? "" : "s"}`}
        actions={<Button asChild data-testid="new-loan-button"><Link to="/admin/loans/new"><Plus /> New loan</Link></Button>} />

      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="inline-flex rounded-md border bg-card p-1" role="tablist" data-testid="loan-status-filter">
          {TABS.map(([v, label]) => (
            <button key={v || "all"} onClick={() => setStatus(v)} role="tab" aria-selected={status === v}
              data-testid={`loan-filter-${v || "all"}`}
              className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${status === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {borrowerId && (
            <button onClick={() => { const n = new URLSearchParams(params); n.delete("borrower_id"); n.delete("borrower_name"); setParams(n); }}
              className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-xs font-medium" data-testid="clear-borrower-filter">
              Borrower: {borrowerName || "selected"} <X className="h-3 w-3" />
            </button>
          )}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search borrower, email, loan #"
              className="h-10 bg-card pl-9" data-testid="loan-search-input" />
          </div>
        </div>
      </div>

      <div className="fade-up overflow-x-auto rounded-lg border bg-card">
        <Table data-testid="loans-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {["Loan #", "Borrower", "Type", "Principal", "APR", "Balance", "Payoff", "Next due", "Status"].map((h) => (
                <TableHead key={h} className="eyebrow h-11 whitespace-nowrap">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((l) => (
              <TableRow key={l.id} onClick={() => navigate(`/admin/loans/${l.id}`)} className="cursor-pointer" data-testid={`loan-row-${l.loan_number}`}>
                <TableCell className="mono text-xs font-medium">{l.loan_number}</TableCell>
                <TableCell>
                  <div className="font-medium">{l.borrower_name}</div>
                  <div className="text-xs text-muted-foreground">{l.borrower_email}</div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm">{l.interest_type === "simple" ? "Simple" : "Amortized"}</TableCell>
                <TableCell className="num">{money(l.principal_cents)}</TableCell>
                <TableCell className="num">{pct(l.apr)}</TableCell>
                <TableCell className="num font-medium">{money(l.principal_balance_cents)}</TableCell>
                <TableCell className="num">{money(l.payoff_cents)}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">
                  {l.next_due_date ? <>{fmtDate(l.next_due_date)}<div className="num text-xs text-muted-foreground">{money(l.next_due_cents)}</div></> : "—"}
                </TableCell>
                <TableCell><StatusBadge status={l.status} testid={`loan-status-${l.loan_number}`} /></TableCell>
              </TableRow>
            ))}
            {!loading && !data.items.length && (
              <TableRow><TableCell colSpan={9} className="py-14 text-center text-sm text-muted-foreground" data-testid="loans-empty">No loans match these filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} testid="loans-pager" />
    </div>
  );
}
